'use client'

import { RazaoReport, getRazaoReport, exportRazao } from '@/lib/api/financial/financial-accounting-api'
import { ChartOfAccountsItem, getChartOfAccounts } from '@/lib/api/financial/financial-chart-of-accounts-api'
import { useEffect, useState } from 'react'
import { MdArrowBack, MdPrint, MdFileDownload } from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

const formatCurrency = (cents: number) =>
    (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const getDefaultDates = () => {
    const now = new Date()
    return {
        startDate: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10),
        endDate: new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10),
    }
}

export default function RazaoReportPage() {
    const navigate = useNavigate()
    const { accountId } = useParams<{ accountId: string }>()
    const [data, setData] = useState<RazaoReport | null>(null)
    const [accounts, setAccounts] = useState<ChartOfAccountsItem[]>([])
    const [selectedAccountId, setSelectedAccountId] = useState(accountId || '')
    const [loading, setLoading] = useState(false)
    const [startDate, setStartDate] = useState(getDefaultDates().startDate)
    const [endDate, setEndDate] = useState(getDefaultDates().endDate)

    useEffect(() => {
        loadAccounts()
    }, [])

    useEffect(() => {
        if (selectedAccountId) loadData()
    }, [selectedAccountId, startDate, endDate])

    const loadAccounts = async () => {
        try {
            const res = await getChartOfAccounts()
            if (res.success && res.result) setAccounts(res.result.filter(a => a.active))
        } catch { /* ignore */ }
    }

    const loadData = async () => {
        if (!selectedAccountId) return
        setLoading(true)
        try {
            const res = await getRazaoReport(parseInt(selectedAccountId, 10), startDate, endDate)
            if (res.success && res.result) setData(res.result)
        } catch { toast.error('Erro ao carregar razão') }
        finally { setLoading(false) }
    }

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}><MdArrowBack size={20} /></button>
                    <h1 className="text-2xl font-bold">Razão Contábil</h1>
                </div>
                <div className="flex items-center gap-1">
                    {selectedAccountId && (
                        <div className="dropdown dropdown-end">
                            <label tabIndex={0} className="btn btn-sm btn-outline gap-1">
                                <MdFileDownload size={16} />
                                Exportar
                            </label>
                            <ul tabIndex={0} className="dropdown-content menu bg-base-100 rounded-box z-50 w-44 p-2 shadow-lg border border-base-300">
                                <li><button onClick={() => { exportRazao(parseInt(selectedAccountId, 10), startDate, endDate, 'xlsx').catch(() => toast.error('Erro ao exportar')) }}>Excel (.xlsx)</button></li>
                                <li><button onClick={() => { exportRazao(parseInt(selectedAccountId, 10), startDate, endDate, 'pdf').catch(() => toast.error('Erro ao exportar')) }}>PDF</button></li>
                            </ul>
                        </div>
                    )}
                    <button className="btn btn-ghost btn-sm" onClick={() => window.print()}><MdPrint size={18} /></button>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4">
                <select
                    className="select select-bordered select-sm flex-1 max-w-sm"
                    value={selectedAccountId}
                    onChange={e => setSelectedAccountId(e.target.value)}
                >
                    <option value="">Selecionar conta</option>
                    {accounts.map(a => (
                        <option key={a.id} value={a.id}>{a.code} - {a.title}</option>
                    ))}
                </select>
                <input type="date" className="input input-bordered input-sm" value={startDate} onChange={e => setStartDate(e.target.value)} />
                <input type="date" className="input input-bordered input-sm" value={endDate} onChange={e => setEndDate(e.target.value)} />
            </div>

            {loading && <div className="flex justify-center py-20"><div className="loading loading-spinner loading-lg text-secondary"></div></div>}

            {!loading && data && (
                <div className="card bg-base-100 shadow print:shadow-none overflow-x-auto">
                    <div className="card-body p-6">
                        <div className="text-center mb-4">
                            <h2 className="text-lg font-bold">Razão Contábil - {data.account.code} {data.account.title}</h2>
                            <p className="text-sm text-base-content/60">
                                {new Date(startDate).toLocaleDateString('pt-BR')} a {new Date(endDate).toLocaleDateString('pt-BR')}
                            </p>
                        </div>

                        <table className="table table-sm">
                            <thead>
                                <tr>
                                    <th>Data</th>
                                    <th>Descrição</th>
                                    <th>Origem</th>
                                    <th className="text-right">Débito</th>
                                    <th className="text-right">Crédito</th>
                                    <th className="text-right">Saldo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.movements.length === 0 && (
                                    <tr><td colSpan={6} className="text-center text-base-content/40 py-8">Nenhuma movimentação</td></tr>
                                )}
                                {data.movements.map((m, i) => (
                                    <tr key={i} className="hover">
                                        <td className="font-mono text-xs">{new Date(m.date).toLocaleDateString('pt-BR')}</td>
                                        <td className="text-sm max-w-[300px] truncate">
                                            {m.description}
                                            {m.billingId && <span className="text-xs text-base-content/40 ml-1">#{m.billingId}</span>}
                                        </td>
                                        <td><span className="badge badge-xs badge-ghost">{m.origin}</span></td>
                                        <td className="text-right font-mono">{m.type === 'DEBIT' ? formatCurrency(m.amount) : '-'}</td>
                                        <td className="text-right font-mono">{m.type === 'CREDIT' ? formatCurrency(m.amount) : '-'}</td>
                                        <td className={`text-right font-mono font-semibold ${m.balance > 0 ? 'text-error' : 'text-success'}`}>
                                            {formatCurrency(Math.abs(m.balance))} {m.balance > 0 ? 'D' : 'C'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="font-bold border-t-2">
                                    <td colSpan={3}>TOTAIS</td>
                                    <td className="text-right font-mono">{formatCurrency(data.totalDebit)}</td>
                                    <td className="text-right font-mono">{formatCurrency(data.totalCredit)}</td>
                                    <td className={`text-right font-mono ${data.finalBalance > 0 ? 'text-error' : 'text-success'}`}>
                                        {formatCurrency(Math.abs(data.finalBalance))} {data.finalBalance > 0 ? 'D' : 'C'}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            )}
        </div>
    )
}
